import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Icon from './Icon';
import './DateTimeField.css';

/**
 * Tarix və saat sahəsi — 24 saatlıq, gün.ay.il formatında.
 *
 * Native <input type="datetime-local"> brauzerin dilinə görə format seçir
 * (ABŞ dilində mm/dd/yyyy və AM/PM). Bunu HTML və ya CSS ilə dəyişmək mümkün
 * deyil, ona görə sahə öz nəzarətimizdədir.
 *
 * Göstərilir:  20.10.2026 14:30
 * Qaytarılır:  2026-10-20T14:30   (datetime-local ilə eyni — backend dəyişmir)
 */

const pad = n => String(n).padStart(2, '0');

// Bugünkü gün "YYYY-MM-DD" şəklində — minDate üçün hazır dəyər
export function bugunISO() {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function toDisplay(iso) {
  if (!iso) return '';
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
  if (!m) return '';
  const [, y, mo, d, h, mi] = m;
  return `${d}.${mo}.${y} ${h}:${mi}`;
}

function toISO(text) {
  const m = String(text).match(/^(\d{2})\.(\d{2})\.(\d{4})\s+(\d{2}):(\d{2})$/);
  if (!m) return null;
  const dd = +m[1], mm = +m[2], yy = +m[3], hh = +m[4], mn = +m[5];
  if (mm < 1 || mm > 12 || dd < 1 || dd > 31 || hh > 23 || mn > 59) return null;
  const dt = new Date(yy, mm - 1, dd, hh, mn);
  if (dt.getDate() !== dd || dt.getMonth() !== mm - 1) return null;
  return `${yy}-${pad(mm)}-${pad(dd)}T${pad(hh)}:${pad(mn)}`;
}

// Yazdıqca nöqtə və iki nöqtəni özü qoyur.
//
// İl iki rəqəmlə yazılanda ("02.11.26 14:20") əvvəlki maska saatın
// rəqəmlərini ilin davamı sayırdı: "02.11.2614 20". Nəticədə sahə dolu
// görünürdü, amma heç bir tarix qaytarmırdı və göndərəndə "məcburi
// sahələri doldurun" çıxırdı. İndi ilin ilk iki rəqəmi yoxlanılır:
// 19/20 deyilsə, il qısa yazılıb — özü tamamlanır, qalan rəqəmlər saata
// keçir.
function mask(raw) {
  const d = raw.replace(/\D/g, '').slice(0, 12);
  const yuz = d.slice(4, 6);
  const qisaIl = yuz.length === 2 && yuz !== '19' && yuz !== '20';
  const il = qisaIl ? '20' + yuz : d.slice(4, 8);
  const saat = (qisaIl ? d.slice(6) : d.slice(8)).slice(0, 4);

  let out = '';
  if (d.length > 0) out += d.slice(0, 2);
  if (d.length >= 3) out += '.' + d.slice(2, 4);
  if (d.length >= 5) out += '.' + il;
  if (saat.length >= 1) out += ' ' + saat.slice(0, 2);
  if (saat.length >= 3) out += ':' + saat.slice(2, 4);
  return out;
}

// Mətndən tarix və saat hissələrini ayırır (natamam olsa da)
function parts(text) {
  const m = String(text).match(/^(\d{2})\.(\d{2})\.(\d{4})(?:\s+(\d{2}):(\d{2}))?/);
  if (!m) return { date: null, h: null, mi: null };
  return { date: { d: m[1], mo: m[2], y: m[3] }, h: m[4] || null, mi: m[5] || null };
}

/* minDate — "YYYY-MM-DD". Verilibsə, ondan əvvəlki günlər təqvimdə
   seçilə bilmir və əllə yazılanda sahə qırmızı olur. Saat sərbəstdir:
   bugünkü gün üçün keçmiş saat da yazmaq olar. */
export default function DateTimeField({ value, onChange, required, disabled, className = '', id, minDate }) {
  const [text, setText] = useState(() => toDisplay(value));
  const [touched, setTouched] = useState(false);
  const [timeOpen, setTimeOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const wrapRef = useRef(null);
  const panelRef = useRef(null);
  const dateRef = useRef(null);

  useEffect(() => {
    const incoming = toDisplay(value);
    if (toISO(text) !== (value || null) && incoming !== text) setText(incoming);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const setBoth = (next) => {
    setText(next);
    const iso = toISO(next);
    if (iso) onChange(iso);
    else if (!next) onChange('');
  };

  const handle = (raw) => setBoth(mask(raw));

  // --- saat paneli ---
  const reposition = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vh = window.innerHeight;
    const flipUp = vh - r.bottom < 250 && r.top > 250;
    const W = 236;
    setPos({
      left: Math.max(8, Math.min(r.right - W, window.innerWidth - W - 8)),
      width: W,
      top: flipUp ? undefined : r.bottom + 5,
      bottom: flipUp ? vh - r.top + 5 : undefined,
    });
  }, []);

  useEffect(() => {
    if (!timeOpen) { setPos(null); return; }
    reposition();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [timeOpen, reposition]);

  useEffect(() => {
    const onDown = e => {
      if (wrapRef.current && wrapRef.current.contains(e.target)) return;
      if (panelRef.current && panelRef.current.contains(e.target)) return;
      setTimeOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const p = parts(text);
  const curH = p.h, curMi = p.mi;

  const pickTime = (h, mi) => {
    const d = p.date;
    if (!d) return;                      // əvvəlcə tarix lazımdır
    setBoth(`${d.d}.${d.mo}.${d.y} ${h}:${mi}`);
  };

  // Oxlarla dəyişmə — saat 1-bir, dəqiqə 5-beş, hər ikisi dövrə vurur
  const bump = (which, dir) => {
    const h = +(curH ?? 9);
    const mi = +(curMi ?? 0);
    if (which === 'h') {
      pickTime(pad((h + dir + 24) % 24), pad(mi));
    } else {
      const step = 5;
      let next = Math.round(mi / step) * step + dir * step;
      let hh = h;
      if (next >= 60) { next = 0; hh = (h + 1) % 24; }
      if (next < 0)   { next = 55; hh = (h - 1 + 24) % 24; }
      pickTime(pad(hh), pad(next));
    }
  };

  const openDatePicker = () => {
    const el = dateRef.current;
    if (!el) return;
    if (el.showPicker) { try { el.showPicker(); return; } catch (e) { /* dəstəklənmir */ } }
    el.focus(); el.click();
  };

  // Tarix düzgün yazılıb, sadəcə saat hələ seçilməyib — bu SƏHV deyil, yarımçıqdır.
  // Belə halda nə narıncı xəbərdarlıq, nə də format izahı göstərilmir.
  // İl ağlabatan aralıqda deyilsə (məs. 2614) bu, yarımçıq yox, səhvdir —
  // əks halda sahə sakit qalıb istifadəçini boş yerə göndərməyə buraxırdı.
  const ilDuzgun = !p.date || (+p.date.y >= 2000 && +p.date.y <= 2099);
  const dateOnly = !!p.date && ilDuzgun && (!curH || !curMi);
  const bad = touched && text.length > 0 && !toISO(text) && !dateOnly && !timeOpen;

  // Keçmiş gün yazılıb? (yalnız gün müqayisə olunur, saat yox)
  const kecmis = !!(minDate && p.date && ilDuzgun &&
    `${p.date.y}-${p.date.mo}-${p.date.d}` < minDate);

  return (
    <div className={'dtf-wrap ' + className} ref={wrapRef}>
      <input
        id={id}
        className={'dtf-input' + (bad || kecmis ? ' invalid' : '')}
        value={text}
        onChange={e => handle(e.target.value)}
        onBlur={() => setTouched(true)}
        placeholder="gg.aa.iiii ss:dd"
        inputMode="numeric"
        maxLength={16}
        required={required}
        disabled={disabled}
        autoComplete="off"
      />

      <button type="button" className="dtf-btn" onClick={openDatePicker}
              disabled={disabled} tabIndex={-1} title="Təqvimdən gün seç">
        <Icon name="calendar" size={15} />
      </button>
      <button type="button" className={'dtf-btn dtf-btn-time' + (timeOpen ? ' on' : '')}
              onClick={() => { if (p.date) setTimeOpen(o => !o); }}
              disabled={disabled || !p.date} tabIndex={-1}
              title={p.date ? 'Saatı seç' : 'Əvvəlcə tarixi yazın'}>
        <Icon name="clock" size={15} />
      </button>

      {/* Yalnız gün seçimi üçün — görünmür */}
      <input
        ref={dateRef}
        type="date"
        className="dtf-hidden"
        tabIndex={-1}
        min={minDate || undefined}
        onChange={e => {
          if (!e.target.value) return;
          const [y, mo, d] = e.target.value.split('-');
          const time = curH && curMi ? `${curH}:${curMi}` : '';
          setBoth(`${d}.${mo}.${y}${time ? ' ' + time : ''}`);
          if (!time) setTimeOpen(true);          // saat yoxdursa dərhal seçdiririk
        }}
      />

      {timeOpen && pos && createPortal(
        <div className="dtf-time" ref={panelRef}
             style={{ left: pos.left, width: pos.width,
                      ...(pos.top !== undefined ? { top: pos.top } : { bottom: pos.bottom }) }}>
          <div className="dtf-time-head">Saat seçin</div>

          <div className="dtf-stepper">
            <div className="dtf-step">
              <button type="button" className="dtf-arrow" onClick={() => bump('h', +1)} aria-label="Saat artır">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor"
                     strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M18 15l-6-6-6 6"/></svg>
              </button>
              <div className="dtf-num">{curH || '--'}</div>
              <button type="button" className="dtf-arrow" onClick={() => bump('h', -1)} aria-label="Saat azalt">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor"
                     strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
              </button>
              <div className="dtf-step-lbl">Saat</div>
            </div>

            <div className="dtf-colon">:</div>

            <div className="dtf-step">
              <button type="button" className="dtf-arrow" onClick={() => bump('m', +1)} aria-label="Dəqiqə artır">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor"
                     strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M18 15l-6-6-6 6"/></svg>
              </button>
              <div className="dtf-num">{curMi || '--'}</div>
              <button type="button" className="dtf-arrow" onClick={() => bump('m', -1)} aria-label="Dəqiqə azalt">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor"
                     strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
              </button>
              <div className="dtf-step-lbl">Dəqiqə</div>
            </div>
          </div>

          <div className="dtf-quick">
            {['08:00', '09:00', '12:00', '14:00', '17:00', '18:00'].map(q => (
              <button type="button" key={q}
                      className={'dtf-quick-btn' + (curH + ':' + curMi === q ? ' sel' : '')}
                      onClick={() => pickTime(q.slice(0, 2), q.slice(3))}>{q}</button>
            ))}
          </div>

          <button type="button" className="dtf-time-done" onClick={() => setTimeOpen(false)}>Hazırdır</button>
        </div>,
        document.body
      )}

      {bad && <span className="dtf-error">Format: gün.ay.il saat:dəqiqə — məs. 20.10.2026 14:30</span>}
      {kecmis && <span className="dtf-error">Keçmiş tarix seçmək olmaz — ən tezi bugünkü gün.</span>}
      {dateOnly && !kecmis && !timeOpen && <span className="dtf-hint">Saatı seçin</span>}
    </div>
  );
}
